import { Prediction } from '../types/predictions';
import { Theme } from '../theme'; // Add this import

interface ChartDataPoint {
  time: string;
  value: number;
}

const CUTOFF_YEAR = 2015;

/**
 * Format price with consistent styling
 * @param price Price value to format
 * @param options Formatting options
 * @returns Formatted price string
 */
interface FormatPriceOptions {
  compact?: boolean;
  locale?: string;
  preserveNegative?: boolean;
  showCurrency?: boolean; // Add this option
  maximumFractionDigits?: number; // Add this option
}

export const formatPrice = (price: number, options: any = {}): string => {
  const { 
    showCurrency = true, 
    minimumFractionDigits = 0, 
    maximumFractionDigits = 0 
  } = options;
  
  if (showCurrency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(price);
  } else {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(price);
  }
};

/**
 * Helper function for formatting prediction ranges
 */
export const formatPrediction = (pred: Prediction): string => {
  // Handle bet predictions - show bet amount with special formatting
  if (pred.prediction_type === 'bet') {
    const betAmount = pred.price_prediction || pred.price_target || pred.price_min;
    if (betAmount) {
      const amount = Number(betAmount);
      return `🎲 Bet: ${formatPrice(amount)}`;
    }
    return '🎲 Betting Wager';
  }
  
  // Handle sentiment tweets - show the actual quote instead of "No price specified"
  if (pred.prediction_type === 'sentiment') {
    return pred.quote || 'Sentiment tweet';
  }
  
  // Hardcoded fix for Peter Zeihan's prediction: if influencer is Peter Zeihan and value is 1000, show -1000
  if (
    (pred.influencer?.name === 'Peter Zeihan' || pred.influencer_id === 12) &&
    (Number(pred.price_target) === 1000 || Number(pred.price_prediction) === 1000)
  ) {
    return formatPrice(-1000);
  }
  // For range predictions
  if (pred.prediction_type === 'range' && pred.price_min && pred.price_max) {
    const min = Number(pred.price_min);
    const max = Number(pred.price_max);
    return `${formatPrice(min)} - ${formatPrice(max)}`;
  }
  // For top predictions - use price_max or fallback to price_target
  if (pred.prediction_type === 'top') {
    const numValue = pred.price_max 
      ? (typeof pred.price_max === 'string' ? parseFloat(pred.price_max) : Number(pred.price_max))
      : pred.price_target 
      ? (typeof pred.price_target === 'string' ? parseFloat(pred.price_target) : Number(pred.price_target))
      : null;
    if (numValue) return formatPrice(numValue);
  }
  // For bottom predictions - use price_min or fallback to price_target
  if (pred.prediction_type === 'bottom') {
    const numValue = pred.price_min 
      ? (typeof pred.price_min === 'string' ? parseFloat(pred.price_min) : Number(pred.price_min))
      : pred.price_target 
      ? (typeof pred.price_target === 'string' ? parseFloat(pred.price_target) : Number(pred.price_target))
      : null;
    if (numValue) return formatPrice(numValue);
  }
  // For target predictions
  if (pred.prediction_type === 'target' && pred.price_target) {
    const numValue = typeof pred.price_target === 'string' 
      ? parseFloat(pred.price_target) 
      : Number(pred.price_target);
    return formatPrice(numValue);
  }
  // For pricetimerange predictions - use price_min (should be equal to price_max)
  if (pred.prediction_type === 'pricetimerange' && pred.price_min) {
    const min = Number(pred.price_min);
    return formatPrice(min);
  }
  // Extract and format numeric value from text prediction (capture negative sign)
  if (pred.price_prediction) {
    // First check if it's a range format like "130.000 - 140.000"
    const rangeMatch = pred.price_prediction.match(/(\d+(?:[.,]\d+)*)\s*-\s*(\d+(?:[.,]\d+)*)/);
    if (rangeMatch) {
      const minPrice = parseFloat(rangeMatch[1].replace(/[.,]/g, ''));
      const maxPrice = parseFloat(rangeMatch[2].replace(/[.,]/g, ''));
      if (!isNaN(minPrice) && !isNaN(maxPrice)) {
        return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
      }
    }
    
    // If not a range, extract single price (capture negative sign)
    const priceMatch = pred.price_prediction.match(/-?[\d,]+/);
    if (priceMatch) {
      const extractedPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
      if (!isNaN(extractedPrice)) {
        return formatPrice(extractedPrice);
      }
    }
  }
  // If all else fails, return the raw text or a fallback
  return pred.price_prediction || 'N/A';
};

export const formatPredictionRange = (prediction: Prediction): string => {
  if (prediction.prediction_type === 'range' && prediction.price_min && prediction.price_max) {
    return `${formatPrice(Number(prediction.price_min))} - ${formatPrice(Number(prediction.price_max))}`;
  }
  
  if (prediction.prediction_type === 'target' && prediction.price_target) {
    return formatPrice(Number(prediction.price_target));
  }
  
  if (prediction.prediction_type === 'pricetimerange' && prediction.price_min) {
    return formatPrice(Number(prediction.price_min));
  }
  
  // Extract price from text prediction if available
  if (prediction.price_prediction) {
    // Ensure we're working with a string before using match
    const predictionText = typeof prediction.price_prediction === 'string' 
      ? prediction.price_prediction 
      : String(prediction.price_prediction);
    
    // First check if it's a range format like "130.000 - 140.000"
    const rangeMatch = predictionText.match(/(\d+(?:[.,]\d+)*)\s*-\s*(\d+(?:[.,]\d+)*)/);
    if (rangeMatch) {
      const minPrice = parseFloat(rangeMatch[1].replace(/[.,]/g, ''));
      const maxPrice = parseFloat(rangeMatch[2].replace(/[.,]/g, ''));
      if (!isNaN(minPrice) && !isNaN(maxPrice)) {
        return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
      }
    }
    
    // If not a range, extract single price
    const match = predictionText.match(/[\d,]+/);
    if (match) {
      const extractedPrice = parseFloat(match[0].replace(/,/g, ''));
      if (!isNaN(extractedPrice)) {
        return formatPrice(extractedPrice);
      }
    }
  }
  
  return 'N/A';
};

export const getInfluencerName = (pred: Prediction): string => {
  const name = pred.influencer?.name || 'Unknown Expert';
  const xHandle = pred.influencer?.x_handle;
  return xHandle ? `${name} (@${xHandle})` : name;
};

export const getPredictionDisplayValue = (pred: Prediction): number => {
  if (pred.price_target) return Number(pred.price_target);
  if (pred.price_min) return Number(pred.price_min);
  return 0;
};

// First, let's examine the functions that process price data in the frontend
export const filterHistoricalData = (data: Array<{ time: string; value: number }>) => {
  // Make sure data is in proper chronological order (oldest to newest)
  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.time).getTime();
    const dateB = new Date(b.time).getTime();
    return dateA - dateB; // This ensures ascending order by date
  });
  
  // Ensure data values are processed correctly but without dividing high values
  const processedData = sortedData.map(item => {
    // No longer dividing high values as Bitcoin prices can legitimately be above 100k
    return item;
  });
  
  return processedData;
};

export const extendChartData = (
  data: Array<{ time: string; value: number }>,
  predictions: Prediction[]
) => {
  // Make sure input data is properly sorted
  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.time).getTime();
    const dateB = new Date(b.time).getTime();
    return dateA - dateB;
  });

  // Combine sorted data with predictions (if needed)
  // For now, just return the sorted data as normalizedData
  const normalizedData = sortedData;

  return normalizedData;
};

export const getPredictionStatusColor = (prediction: Prediction, theme: Theme): string => {
  switch(prediction.success_status) {
    case 'success': return theme.successText;
    case 'miss': return theme.failedText; // Changed from 'failed' to 'miss'
    default: return theme.primary;
  }
};
